import { useState, useEffect, useRef, useCallback } from 'react';

export const useSound = () => {
    const [soundEnabled, setSoundEnabled] = useState(() => {
        const stored = localStorage.getItem('pokerTimerSound');
        return stored ? stored === 'true' : true;
    });

    // Nowy stan dla wyciszenia
    const [muted, setMuted] = useState(() => {
        const stored = localStorage.getItem('pokerTimerMuted');
        return stored ? stored === 'true' : false;
    });

    const tickSound = useRef(null);
    const alarmSound = useRef(null);
    const countdownSound = useRef(null);
    const warningSound = useRef(null);

    // Flaga dla interakcji użytkownika
    const userInteractedRef = useRef(false);

    // Inicjalizacja dźwięków z poprawnymi ustawieniami
    useEffect(() => {
        // Funkcja do tworzenia obiektu Audio z odpowiednimi ustawieniami
        const createAudio = (src) => {
            const audio = new Audio(src);
            audio.preload = 'auto'; // Preładowanie dźwięku
            return audio;
        };

        tickSound.current = createAudio('/assets/sounds/tick.mp3');
        alarmSound.current = createAudio('/assets/sounds/alarm.mp3');
        countdownSound.current = createAudio('/assets/sounds/countdown.mp3');
        warningSound.current = createAudio('/assets/sounds/warning.mp3');

        // Funkcja, która oznaczy, że użytkownik wchodził w interakcję
        const markUserInteraction = () => {
            userInteractedRef.current = true;

            // Ładowanie dźwięków po interakcji użytkownika
            if (tickSound.current) tickSound.current.load();
            if (alarmSound.current) alarmSound.current.load();
            if (countdownSound.current) countdownSound.current.load();
            if (warningSound.current) warningSound.current.load();

            // Usuwamy listenery po pierwszej interakcji
            document.removeEventListener('click', markUserInteraction);
            document.removeEventListener('touchstart', markUserInteraction);
        };

        // Dodajemy listenery do wykrycia interakcji użytkownika
        document.addEventListener('click', markUserInteraction);
        document.addEventListener('touchstart', markUserInteraction);

        // Czyszczenie przy odmontowaniu
        return () => {
            document.removeEventListener('click', markUserInteraction);
            document.removeEventListener('touchstart', markUserInteraction);

            tickSound.current = null;
            alarmSound.current = null;
            countdownSound.current = null;
            warningSound.current = null;
        };
    }, []);

    // Zapisanie preferencji dźwięku w localStorage
    useEffect(() => {
        localStorage.setItem('pokerTimerSound', soundEnabled.toString());
    }, [soundEnabled]);

    // Zapisanie stanu wyciszenia w localStorage
    useEffect(() => {
        localStorage.setItem('pokerTimerMuted', muted.toString());
    }, [muted]);

    // Funkcja bezpiecznego odtwarzania dźwięku
    const safePlaySound = useCallback((soundRef) => {
        if (!soundRef.current || !soundEnabled || muted) return;

        // Resetujemy dźwięk na początek
        soundRef.current.currentTime = 0;

        // Catch błędów odtwarzania i wyciszanie/głośność
        const playPromise = soundRef.current.play();

        if (playPromise !== undefined) {
            playPromise.catch(error => {
                console.log('Error playing sound:', error);
                // Automatyczne oznaczenie interakcji przy pierwszym błędzie
                userInteractedRef.current = true;
            });
        }
    }, [soundEnabled, muted]);

    // Funkcje odtwarzania dźwięków
    const playTick = useCallback(() => {
        safePlaySound(tickSound);
    }, [safePlaySound]);

    const playAlarm = useCallback(() => {
        safePlaySound(alarmSound);
    }, [safePlaySound]);

    const playCountdown = useCallback(() => {
        safePlaySound(countdownSound);
    }, [safePlaySound]);

    const playWarning = useCallback(() => {
        safePlaySound(warningSound);
    }, [safePlaySound]);

    const toggleSound = () => {
        setSoundEnabled(!soundEnabled);
    };

    const toggleMute = () => {
        setMuted(!muted);
    };

    return {
        soundEnabled,
        toggleSound,
        muted,
        toggleMute,
        playTick,
        playAlarm,
        playCountdown,
        playWarning
    };
};